import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { Avatar } from "@/components/AppHeader";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, ChevronLeft, Send, MessageSquare, Search as SearchIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";

function timeShort(iso, lang) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return lang === "ar" ? "الآن" : lang === "he" ? "עכשיו" : "now";
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  if (d < 604800) return `${Math.floor(d / 86400)}d`;
  return `${Math.floor(d / 604800)}w`;
}

export default function MessagesPage() {
  const { id: convId } = useParams();
  if (convId) return <ChatView convId={convId} />;
  return <ConversationsList />;
}

function ConversationsList() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  const load = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConvs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000); // soft poll every 15s
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!filter.trim()) return convs;
    const q = filter.toLowerCase();
    return convs.filter((c) => (c.participant?.name || "").toLowerCase().includes(q));
  }, [convs, filter]);

  const startNewChat = async () => {
    const email = window.prompt(L("اكتب بريد المستلم", "אימייל הנמען", "Recipient email")) || "";
    if (!email.trim()) return;
    try {
      const { data: users } = await api.get(`/search?q=${encodeURIComponent(email.trim())}`);
      const target = (users?.users || []).find((u) => (u.email || "").toLowerCase() === email.trim().toLowerCase());
      if (!target) { toast.error(L("لم يتم العثور على المستخدم", "המשתמש לא נמצא", "User not found")); return; }
      const msg = window.prompt(L("اكتب رسالتك", "כתוב הודעה", "Your message"), L("مرحباً!", "שלום!", "Hi!"));
      if (!msg) return;
      const { data } = await api.post("/messages", { recipient_id: target.id, content: msg });
      navigate(`/messages/${data.conversation_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <MobileShell
      topBar={
        <div className="h-14 px-4 flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            data-testid="msg-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1">{t("messages_title", language)}</h1>
          <button
            onClick={startNewChat}
            data-testid="msg-new-btn"
            className="te-btn-primary !py-2 !px-3 text-[13px]"
          >
            {L("جديد", "חדש", "New")}
          </button>
        </div>
      }
    >
      <div className="px-4 pt-3 pb-2">
        <div className="relative">
          <SearchIcon size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[var(--te-text-muted)]" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={L("ابحث في المحادثات…", "חיפוש שיחות…", "Search conversations…")}
            className="te-input !pl-9 !py-2.5 text-sm"
            data-testid="msg-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="px-6 py-16 text-center" data-testid="msg-empty">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--te-primary-soft)] flex items-center justify-center mb-4">
            <MessageSquare size={26} className="text-[var(--te-primary)]" />
          </div>
          <h2 className="font-extrabold text-[18px] text-[var(--te-text)]">{L("لا توجد محادثات", "אין שיחות", "No conversations yet")}</h2>
          <p className="text-[13px] text-[var(--te-text-secondary)] mt-2 max-w-xs mx-auto">
            {L("ابدأ محادثة جديدة من زر «جديد».", "התחל שיחה חדשה דרך כפתור “חדש”.", 'Tap "New" to start a conversation.')}
          </p>
        </div>
      ) : (
        <div className="px-3 pb-4">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/messages/${c.id}`}
              data-testid={`conv-${c.id}`}
              className="block p-3 rounded-2xl hover:bg-[var(--te-bg-soft)] flex items-center gap-3 te-fade-up"
            >
              <Avatar user={c.participant} size={48} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-[15px] text-[var(--te-text)] truncate" dir="auto">{c.participant?.name || "—"}</span>
                  <span className="text-[11px] text-[var(--te-text-muted)] shrink-0">{timeShort(c.updated_at, language)}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[13px] truncate ${c.unread > 0 ? "text-[var(--te-text)] font-semibold" : "text-[var(--te-text-secondary)]"}`} dir="auto">
                    {c.last_message}
                  </span>
                  {c.unread > 0 && (
                    <span className="shrink-0 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[var(--te-primary)] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`unread-${c.id}`}>
                      {c.unread}
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </MobileShell>
  );
}

function ChatView({ convId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useI18n();
  const me = user?.id;
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const listRef = useRef(null);

  const L = (ar, he, en) => language === "ar" ? ar : language === "he" ? he : en;

  const load = async (markRead = true) => {
    try {
      const [c, m] = await Promise.all([
        api.get(`/conversations/${convId}`),
        api.get(`/conversations/${convId}/messages`),
      ]);
      setConv(c.data); setMsgs(m.data);
      if (markRead) await api.post(`/conversations/${convId}/read`).catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    setLoading(true); load();
    const id = setInterval(() => load(false), 8000);
    return () => clearInterval(id);
  }, [convId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || !conv?.participant?.id) return;
    setBusy(true);
    try {
      const { data: m } = await api.post("/messages", { recipient_id: conv.participant.id, content: text });
      setInput("");
      setMsgs((cur) => [...cur, { id: m.id, sender_id: me, recipient_id: conv.participant.id, content: m.content, created_at: m.created_at, read: false }]);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <MobileShell
      padBottom={false}
      topBar={
        <div className="h-14 px-3 flex items-center gap-2 border-b border-[var(--te-divider)]">
          <button
            onClick={() => navigate("/messages")}
            data-testid="chat-back-btn"
            className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
          >
            <ChevronLeft size={20} />
          </button>
          {conv?.participant ? (
            <Link to={`/profile/${conv.participant.id}`} className="flex items-center gap-2.5 flex-1 min-w-0">
              <Avatar user={conv.participant} size={36} />
              <span className="font-bold text-[15px] text-[var(--te-text)] truncate" dir="auto">{conv.participant.name}</span>
            </Link>
          ) : (
            <div className="flex-1 h-6 bg-[var(--te-input-bg)] rounded animate-pulse" />
          )}
        </div>
      }
    >
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-4 te-no-scrollbar pb-2" style={{ maxHeight: "calc(100% - 60px)" }}>
        {loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
        ) : msgs.length === 0 ? (
          <div className="text-center text-sm text-[var(--te-text-muted)] py-8">
            {L("ابدأ المحادثة!", "התחל לשוחח!", "Start the conversation!")}
          </div>
        ) : (
          <div className="space-y-1.5">
            {msgs.map((m) => {
              const mine = String(m.sender_id) === String(me);
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? "justify-end" : "justify-start"}`}
                  data-testid={`msg-${m.id}`}
                >
                  <div
                    className={`max-w-[78%] px-3.5 py-2 rounded-2xl text-[14px] leading-snug whitespace-pre-wrap ${
                      mine
                        ? "bg-[var(--te-primary)] text-white rounded-br-md"
                        : "bg-[var(--te-input-bg)] text-[var(--te-text)] rounded-bl-md"
                    }`}
                    dir="auto"
                  >
                    {m.content}
                    <div className={`text-[10px] mt-0.5 text-right ${mine ? "text-white/70" : "text-[var(--te-text-muted)]"}`} dir="ltr">
                      {timeShort(m.created_at, language)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <form
        onSubmit={send}
        className="sticky bottom-0 bg-white border-t border-[var(--te-divider)] p-3 flex items-end gap-2"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)" }}
      >
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
          placeholder={L("اكتب رسالة…", "כתוב הודעה…", "Type a message…")}
          rows={1}
          className="te-input flex-1 !py-2.5 text-sm resize-none max-h-[120px]"
          data-testid="chat-input"
          dir="auto"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          data-testid="chat-send-btn"
          className="te-btn-primary !w-11 !h-11 !p-0 shrink-0"
          aria-label={L("إرسال", "שלח", "Send")}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </form>
    </MobileShell>
  );
}
